import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Delete, Message, Schedule } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { excluirAgendamentosIzza } from "../../../services/enviosIzza"
import useLoadingStore from "../../../stores/LoadingStore"
import { useVerEnviosIzzaContext } from "../context/useVerEnviosIzzaContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "950px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    mensagemCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 300,
    },
    semDados: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
    botaoExcluir: {
        borderRadius: 25,
        backgroundColor: "var(--cor-erro)",
        color: "white",
        "&:hover": { backgroundColor: "var(--cor-erro-hover)" },
        boxShadow: "0 2px 8px rgba(229,57,53,0.4)",
    },
    enviado: {
        color: "#4caf50",
        fontWeight: 500,
    },
    pendente: {
        color: "#7f8c8d",
    },
}))

function formatarData(timestamp: number | null): string {
    if (timestamp === null || timestamp === undefined) return "—"
    return new Date(timestamp).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

function statusEnvio(enviado: number): string {
    if (enviado === 1) return "Enviado"
    if (enviado === -1) return "Expirado"
    return "Pendente"
}

export function TabelaMensagensIzza() {
    const classes = useStyles()
    const { dados, erroCarregamento } = useVerEnviosIzzaContext()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)
    const [confirmando, setConfirmando] = useState(false)

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const handleExcluirAgendamentos = async () => {
        if (!confirmando) {
            setConfirmando(true)
            return
        }
        setConfirmando(false)
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await excluirAgendamentosIzza(autorizacao)
            if ("retornoErro" in response) {
                enqueueSnackbar(response.mensagem || "Erro ao excluir agendamentos", {
                    variant: "error",
                })
            } else {
                enqueueSnackbar(`Mensagens agendadas excluídas: ${response.excluidos}`, {
                    variant: "success",
                })
            }
        } catch {
            enqueueSnackbar("Erro ao excluir agendamentos", { variant: "error" })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" className={classes.titleText}>
                        Mensagens Izza
                    </Typography>
                    <Button
                        variant="contained"
                        className={classes.botaoExcluir}
                        startIcon={<Delete />}
                        onClick={handleExcluirAgendamentos}
                    >
                        {confirmando
                            ? "Clique novamente para confirmar"
                            : "Excluir mensagens agendadas"}
                    </Button>
                </Box>

                {/* Tabela de mensagens agendadas */}
                <Box className={classes.configCard} component={Paper}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            <Schedule className={classes.iconHeader} />
                            Mensagens Agendadas
                        </Typography>
                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                            {dados.agendadas.length} mensagem(ns)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 400, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ width: "100%", minWidth: 620 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Data</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 160 }}>
                                            <strong>Destinatário</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220 }}>
                                            <strong>Mensagem</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Status</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.agendadas.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className={classes.semDados}>
                                                Nenhuma mensagem agendada.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.agendadas.map((msg, idx) => (
                                            <TableRow key={String(msg.id) + idx} hover>
                                                <TableCell>{formatarData(msg.data)}</TableCell>
                                                <TableCell
                                                    className={classes.mensagemCell}
                                                    title={msg.destinatario}
                                                >
                                                    {msg.destinatario}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.mensagemCell}
                                                    title={msg.mensagem}
                                                >
                                                    {msg.mensagem}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        msg.enviado === 1
                                                            ? classes.enviado
                                                            : classes.pendente
                                                    }
                                                >
                                                    {statusEnvio(msg.enviado)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>

                {/* Tabela de mensagens enviadas */}
                <Box className={classes.configCard} component={Paper}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            <Message className={classes.iconHeader} />
                            Mensagens Enviadas
                        </Typography>
                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                            {dados.enviadas.length} mensagem(ns)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ width: "100%", minWidth: 620 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Data</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 160 }}>
                                            <strong>Destinatário</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220 }}>
                                            <strong>Mensagem</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Status</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.enviadas.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className={classes.semDados}>
                                                Nenhuma mensagem enviada.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.enviadas.map(msg => (
                                            <TableRow key={msg.id} hover>
                                                <TableCell>{formatarData(msg.data)}</TableCell>
                                                <TableCell
                                                    className={classes.mensagemCell}
                                                    title={msg.destinatario}
                                                >
                                                    {msg.destinatario}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.mensagemCell}
                                                    title={msg.mensagem}
                                                >
                                                    {msg.mensagem}
                                                </TableCell>
                                                <TableCell className={classes.enviado}>
                                                    {statusEnvio(msg.enviado)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
